import { BREAKDOWNS } from 'features/charts-v2/types'
import type {
  Breakdown,
  CustomBreakdown as CustomBreakdownV2,
  GroupableBreakdown,
  NonGroupableBreakdown,
} from 'features/charts-v2/types/filter-hash/v2'

/**
 * Check if breakdown is a groupable standard breakdown (brand, product, or theme).
 * Only these three carry the `grouped: boolean` field.
 */
export function isGroupableBreakdown(
  breakdown: Breakdown | null | undefined,
): breakdown is GroupableBreakdown {
  return (
    breakdown != null &&
    breakdown.kind === 'standard' &&
    (breakdown.id === 'brand' ||
      breakdown.id === 'product' ||
      breakdown.id === 'theme')
  )
}

/**
 * Pluralized label for the "Group {breakdown}" toggle.
 * Matches the charts-v2 BreakdownCard toggle text exactly.
 */
export function getGroupToggleLabel(breakdown: GroupableBreakdown): string {
  if (breakdown.id === 'brand') return 'Group brands'
  if (breakdown.id === 'product') return 'Group products'
  return 'Group themes'
}

/**
 * Selection result can be:
 * - null: "None" option
 * - Breakdown: v2 breakdown object (standard or custom)
 */
export type BreakdownSelection = Breakdown | null

/**
 * Helper to check if a breakdown is a custom breakdown
 */
export function isCustomBreakdown(
  selection: Breakdown,
): selection is CustomBreakdownV2 {
  return selection.kind === 'custom'
}

/**
 * Helper to check if a breakdown is a standard breakdown
 */
export function isStandardBreakdown(
  selection: Breakdown,
): selection is GroupableBreakdown | NonGroupableBreakdown {
  return selection.kind === 'standard'
}

/**
 * Get the identifying string for a breakdown (id for standard, field for custom)
 */
export function getBreakdownId(breakdown: Breakdown): string {
  return breakdown.kind === 'standard' ? breakdown.id : breakdown.field
}

/**
 * Check if breakdown is the sentiment breakdown
 */
export function isSentimentBreakdown(
  breakdown: Breakdown | null,
): breakdown is NonGroupableBreakdown {
  return (
    breakdown !== null &&
    breakdown.kind === 'standard' &&
    breakdown.id === 'sentiment'
  )
}

/**
 * Check if breakdown is a theme breakdown
 */
export function isThemeBreakdown(
  breakdown: Breakdown | null,
): breakdown is GroupableBreakdown {
  return (
    breakdown !== null &&
    breakdown.kind === 'standard' &&
    breakdown.id === 'theme'
  )
}

/**
 * Check if breakdown is the rating breakdown
 */
export function isRatingBreakdown(
  breakdown: Breakdown | null,
): breakdown is NonGroupableBreakdown {
  return (
    breakdown !== null &&
    breakdown.kind === 'standard' &&
    breakdown.id === 'rating'
  )
}

/**
 * Check if breakdown is the promotional breakdown
 */
export function isPromotionalBreakdown(
  breakdown: Breakdown | null,
): breakdown is NonGroupableBreakdown {
  return (
    breakdown !== null &&
    breakdown.kind === 'standard' &&
    breakdown.id === 'promotional'
  )
}

/**
 * Check if breakdown is the source type breakdown
 */
export function isSourceTypeBreakdown(
  breakdown: Breakdown | null,
): breakdown is NonGroupableBreakdown {
  return (
    breakdown !== null &&
    breakdown.kind === 'standard' &&
    breakdown.id === 'source_type'
  )
}

/**
 * Check if breakdown is a dataset breakdown
 */
/**
 * Get the human-readable label for a breakdown selection.
 */
export function getBreakdownLabel(breakdown: BreakdownSelection): string {
  if (!breakdown) return 'Unknown'
  if (breakdown.kind === 'custom') {
    return breakdown.alias || breakdown.field
  }
  if (breakdown.kind === 'standard') {
    const config = Object.values(BREAKDOWNS).find((b) => b.id === breakdown.id)
    return config?.label || breakdown.id
  }
  return 'Unknown'
}

export function isDatasetBreakdown(
  breakdown: Breakdown | null,
): breakdown is NonGroupableBreakdown {
  return (
    breakdown !== null &&
    breakdown.kind === 'standard' &&
    breakdown.id === 'dataset'
  )
}

/**
 * Returns the custom-breakdown field name at position `index` (for display aliasing), or
 * undefined when the breakdown at that position is absent, null, or a standard breakdown.
 *
 * Accepts `readonly unknown[]` so it is usable from components that type selectedBreakdowns
 * loosely (e.g. SummaryMetricsTable).
 */
export function customBreakdownFieldAt(
  breakdowns: readonly unknown[] | undefined,
  index: number,
): string | undefined {
  const b = breakdowns?.[index]
  if (b == null || typeof b !== 'object') return undefined
  const bd = b as Breakdown
  return bd.kind === 'custom' ? (bd as CustomBreakdownV2).field : undefined
}
